import { Component } from 'react';
import { connect } from 'react-redux';
import { debounce, difference } from 'underscore';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import UILink from 'UIComponents/link/UILink';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { FONT_FORMAT_LIST as SYSTEM_FONTS, FONT_SETS, UNSUPPORTED_FONTS } from 'ContentUtils/constants/Fonts';
import { ASYNC_SELECTOR_DEBOUNCE, DEFAULT_PAGE_SIZE } from 'ContentComponents/cmv2/constants';
import * as RequestStatus from 'ContentUtils/constants/RequestStatus';
import { FONT } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { combineFonts, getDefaultVariant } from 'ContentComponents/cmv2/fontPicker/utils';
import AsyncSelector from 'ContentComponents/cmv2/components/AsyncSelector';
import GoogleFontOption from 'ContentComponents/cmv2/fontPicker/GoogleFontOption';
import CustomFontOption from 'ContentComponents/cmv2/fontPicker/CustomFontOption';
import { FontOption as DefaultFontOption } from './FontOption';
import BrandingCustomFontOption from 'ContentComponents/cmv2/fontPicker/BrandingCustomFontOption';
import * as FontsActions from 'ContentData/actions/Fonts';
import * as fontsSelectors from 'ContentData/selectors/fonts';
import CustomFontsUploadModal from '../../modals/customFonts/CustomFontsUploadModal';
import { ModalPages } from 'ContentData/constants/fonts';
import { Button } from 'foundations-components/transitional/components/Button';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.cmv2.fields.font.${key}`;
const flattenNestedObjectArray = (newFlatArray, fontList) => {
  // fontList is a nested array of objects, `options` can either be true, or another fontList array (simplified):
  // [
  //   { options: true, text: 'FontName', value: 'FontFamilyList' },
  //   {
  //     text: 'TitleOfSubgroup',
  //     options: [{ options: true, text: 'FontName', value: 'FontFamilyList' }, {...}],
  //   },
  // ]

  if (fontList === undefined || fontList === true) return newFlatArray;
  if (Array.isArray(fontList)) {
    return fontList.reduce(flattenNestedObjectArray, newFlatArray);
  }
  if (!Array.isArray(fontList.options)) newFlatArray.push(fontList);
  return flattenNestedObjectArray(newFlatArray, fontList.options);
};
const mapStateToProps = (state, props) => {
  const {
    modulePath,
    themePath,
    childTheme
  } = props;
  const assetPath = modulePath ? childTheme || modulePath.replace(/^\/+/, '') : themePath;
  return {
    assetPath,
    commonGoogleFontResults: fontsSelectors.getCommonGoogleFontResults(state),
    commonGoogleFonts: fontsSelectors.getCommonGoogleFonts(state),
    commonGoogleFontsRequestStatus: fontsSelectors.getCommonFontsFetchStatus(state),
    customFontByAssetOptions: fontsSelectors.getCustomFontsByAssetOptions(state, {
      assetPath
    }),
    customFontOptions: fontsSelectors.getCustomFontsOptions(state),
    customFontsByAsset: fontsSelectors.getCustomFontsByAsset(state, {
      assetPath
    }),
    customFontsByAssetRequestStatus: fontsSelectors.getCustomFontsByAssetFetchStatus(state, {
      assetPath
    }),
    customFonts: fontsSelectors.getCustomFonts(state),
    customFontsRequestStatus: fontsSelectors.getCustomFontsFetchStatus(state),
    googleFontOptions: fontsSelectors.getOptions(state),
    googleFonts: fontsSelectors.getFonts(state),
    selectedCustomFonts: fontsSelectors.getSelectedCustomFonts(state),
    selectedGoogleFonts: fontsSelectors.selectedGoogleFonts(state)
  };
};
const mapDispatchToProps = {
  fetchCustomFontByAsset: FontsActions.fetchCustomFontByAsset,
  fetchCustomFont: FontsActions.fetchCustomFont,
  fetchCustomFontsByAsset: FontsActions.fetchCustomFontsByAsset,
  fetchCustomFonts: FontsActions.fetchCustomFonts,
  fetchGoogleFont: FontsActions.fetchFont,
  fetchGoogleFonts: FontsActions.fetchFonts,
  clearGoogleFonts: FontsActions.clearFonts,
  selectCustomFont: FontsActions.selectCustomFont,
  selectGoogleFont: FontsActions.selectFont,
  fetchCommonGoogleFonts: FontsActions.fetchCommonFonts
};
const connector = connect(mapStateToProps, mapDispatchToProps);
const SYSTEM_FONT_VALUES = Object.values(SYSTEM_FONTS || {});
const UNSUPPORTED_FONT_VALUES = Object.values(UNSUPPORTED_FONTS || {});
class FontFamilyPicker extends Component {
  constructor(props) {
    super(props);
    this.getLimitedOptions = limitedOptions => {
      const {
        customFontByAssetOptions,
        customFontOptions,
        googleFontOptions,
        showCustomFonts = true
      } = this.props;
      const combinedCustomFontOptions = [...customFontByAssetOptions, ...customFontOptions];
      const combinedFontOptions = [...(showCustomFonts ? combinedCustomFontOptions : []), ...googleFontOptions, ...this.systemFonts];
      const optionMap = combinedFontOptions.reduce((hash, font) => Object.assign({}, hash, {
        [font.text]: font
      }), {});
      const validOptions = limitedOptions.reduce((hash, fontName) => {
        const validOption = optionMap[fontName];
        /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
        return validOption ? [...hash, validOption] : hash;
      }, []);
      return validOptions;
    };
    this.togglePreviewPointerEvents = setting => {
      const previewIframe = document && document.querySelector('.preview-iframe-wrapper');
      if (previewIframe) previewIframe.style.pointerEvents = setting;
    };
    if (props.fontList) {
      this.fontListArray = flattenNestedObjectArray([], Object.values(props.fontList));
    }
    this.getFontListOption = this.getFontListOption.bind(this);
    this.loadOptions = this.loadOptions.bind(this);
    this.onFontSelect = this.onFontSelect.bind(this);
    this.renderDisplayFont = this.renderDisplayFont.bind(this);
    this.renderFontPickerOption = this.renderFontPickerOption.bind(this);
    this.renderFontPickerValue = this.renderFontPickerValue.bind(this);
    this.handleCancelCustomUpload = this.handleCancelCustomUpload.bind(this);
    this.handleCustomUploadClick = this.handleCustomUploadClick.bind(this);
    this.trackCustomFontSelect = this.trackCustomFontSelect.bind(this);
    this.debouncedLoadOptionsWithFontFetching = debounce(this.loadOptionsWithFontFetching, ASYNC_SELECTOR_DEBOUNCE);
    this.debouncedLoadLimitedOptionsWithFontFetching = debounce(this.loadLimitedOptionsWithFontFetching, ASYNC_SELECTOR_DEBOUNCE);
    this.state = {
      input: '',
      customUploadModalOpen: false
    };
  }
  componentDidMount() {
    const {
      assetPath,
      fetchCustomFontByAsset,
      fetchCustomFont,
      fetchCustomFonts,
      fetchGoogleFont,
      value
    } = this.props;
    const {
      font,
      font_set,
      fontId
    } = value || {};
    if (font && !SYSTEM_FONT_VALUES.includes(font) && !UNSUPPORTED_FONT_VALUES.includes(font)) {
      let fontName = font;
      if (this.fontListArray) {
        const queryFont = this.fontListArray.find(fontObject => fontObject.value === font);
        fontName = queryFont && queryFont.text || font;
      }
      if (font_set === FONT_SETS.CUSTOM) {
        fetchCustomFontByAsset({
          family: font,
          path: assetPath
        }).catch(err => {
          throw err;
        });
      } else if (font_set === FONT_SETS.BRANDING) {
        if (fontId) {
          fetchCustomFont(fontId).catch(err => {
            throw err;
          });
        } else {
          // if a BRANDING font is selected but there is no fontId in the value object,
          // fallback to just fetch all branding fonts so picker can find selected font data
          // this can happen with inherited values using property paths and id is not included
          fetchCustomFonts(font).catch(err => {
            throw err;
          });
        }
      } else if (font_set === FONT_SETS.GOOGLE) {
        fetchGoogleFont(fontName).catch(err => {
          throw err;
        });
      }
    }
    this.total = null;
    this.hasMore = true;
  }
  get seleniumKey() {
    return this.props.fieldKey.join('.');
  }
  get systemFonts() {
    return Object.keys(SYSTEM_FONTS).map(fontKey => {
      return {
        fontSet: FONT_SETS.DEFAULT,
        text: fontKey,
        value: SYSTEM_FONTS[fontKey]
      };
    });
  }
  get commonGoogleFontOptions() {
    const {
      commonGoogleFontResults
    } = this.props;
    const googleFontOptions = commonGoogleFontResults.map(font => {
      const {
        family
      } = font;
      return {
        fontSet: FONT_SETS.GOOGLE,
        text: family,
        value: family
      };
    });
    return combineFonts({
      googleFontOptions,
      systemFonts: this.systemFonts
    });
  }
  get allGoogleFonts() {
    return Object.assign({}, this.props.commonGoogleFonts, this.props.googleFonts, this.props.selectedGoogleFonts);
  }
  get defaultOptions() {
    const {
      value
    } = this.props;
    const {
      font_set
    } = value || {};
    let unsupportedFont = {};
    if (this.fontListArray) {
      return this.fontListArray.map(this.getFontListOption);
    } else if (!SYSTEM_FONT_VALUES.includes(this.font) && !UNSUPPORTED_FONT_VALUES.includes(this.font)) {
      return [{
        fontSet: font_set,
        text: this.font,
        value: this.font
      }];
    }
    if (UNSUPPORTED_FONT_VALUES.includes(this.font)) {
      const unsupportedFontName = Object.keys(UNSUPPORTED_FONTS).find(fontKey => UNSUPPORTED_FONTS[fontKey] === this.font);
      unsupportedFont = {
        text: unsupportedFontName,
        value: this.font
      };
    }
    return [unsupportedFont, ...this.systemFonts];
  }
  get font() {
    const {
      value
    } = this.props;
    return value && value.font ? value.font : null;
  }
  trackCustomFontSelect(fontFamily, customFontType) {
    const {
      trackFieldAction
    } = this.props;
    if (trackFieldAction) {
      trackFieldAction('custom-fonts-select-font', {
        fontFamily,
        customFontType
      });
    }
  }
  getFontListOption(fontListOption) {
    const {
      selectedCustomFonts
    } = this.props;
    if (fontListOption.options) return fontListOption;
    let fontSet;
    if (SYSTEM_FONTS[fontListOption.text]) {
      fontSet = FONT_SETS.DEFAULT;
    } else if (selectedCustomFonts[fontListOption.text]) {
      fontSet = FONT_SETS.CUSTOM;
    } else {
      fontSet = FONT_SETS.GOOGLE;
    }
    return {
      fontSet,
      text: fontListOption.text,
      value: fontListOption.value
    };
  }
  getGroupedOptions(input) {
    const {
      customFontByAssetOptions,
      customFontOptions,
      googleFontOptions,
      showCustomFonts = true
    } = this.props;

    // TODO This combines old and new kinds of custom fonts so that old theme dev added is just
    // sitting on top of marketer branding uploads as one list under Custom Fonts group.
    // Do we want to separate the different kinds of custom fonts into their own groups?
    // Do we wnat to add any sorting like aphabetical (now it is most->least recently added, endpoint default)
    // Also is it weird that only the marketer uploaded fonts appear in the custom font modal manage page
    // if they are group together like this in the picker?
    // Should we limit number of custom fonts loaded and use load more like google fonts?
    const combinedCustomFontOptions = customFontByAssetOptions && customFontByAssetOptions.concat(customFontOptions);
    const allFontOptions = combineFonts(Object.assign({
      googleFontOptions,
      systemFonts: this.systemFonts
    }, showCustomFonts && {
      customFontOptions: combinedCustomFontOptions
    }), this.hasMore, input);
    let options = [];
    if (!input || !input.length) {
      options = [{
        text: unescapedText(i18nKey('commonFonts')),
        options: this.commonGoogleFontOptions
      }, {
        text: unescapedText(i18nKey('allFonts')),
        options: allFontOptions
      }];
      if (showCustomFonts) {
        options.unshift({
          text: unescapedText(i18nKey('customFonts')),
          options: combinedCustomFontOptions
        });
      }
    } else {
      options = allFontOptions;
    }
    return options;
  }
  loadOptionsWithFontFetching(input, callback, pagination) {
    const {
      assetPath,
      clearGoogleFonts,
      commonGoogleFontsRequestStatus,
      fetchCommonGoogleFonts,
      fetchCustomFontsByAsset,
      fetchCustomFonts,
      fetchGoogleFonts
    } = this.props;
    let offset = pagination.offset;
    if (input !== this.state.input) {
      this.setState({
        input
      });
      clearGoogleFonts();
      offset = 0;
    }
    const query = {
      search: input,
      limit: DEFAULT_PAGE_SIZE,
      offset
    };
    const googleFontsRequest = fetchGoogleFonts(query);
    const customFontByAssetRequest = fetchCustomFontsByAsset({
      path: assetPath
    });
    const customFontRequest = fetchCustomFonts();
    const commonFontsRequest = commonGoogleFontsRequestStatus === RequestStatus.UNINITIALIZED ? fetchCommonGoogleFonts({
      common: true
    }) : Promise.resolve();
    const fontRequests = [googleFontsRequest, customFontByAssetRequest, commonFontsRequest, customFontRequest];
    Promise.all(fontRequests).then(([queriedResponse]) => {
      const {
        total: length,
        hasMore
      } = queriedResponse;
      this.total = length;
      this.hasMore = hasMore;
      const options = this.getGroupedOptions(input);
      callback(null, {
        options,
        pagination: {
          hasMore,
          offset: offset + DEFAULT_PAGE_SIZE,
          length
        }
      });
    }).catch(err => {
      throw err;
    });
  }
  loadLimitedOptionsWithFontFetching(limitedOptions, callback) {
    const {
      assetPath,
      fetchCustomFontsByAsset,
      fetchCustomFonts,
      fetchGoogleFont
    } = this.props;
    const customFontByAssetRequest = fetchCustomFontsByAsset({
      path: assetPath
    });
    const customFontRequest = fetchCustomFonts();
    Promise.all([customFontRequest, customFontByAssetRequest]).then(([queriedResponse]) => {
      //filter out non google fonts from the limited options
      const fetchedFontNames = queriedResponse.map(font => font.name || font.family);
      const googleOptions = difference(limitedOptions, Object.keys(SYSTEM_FONTS), fetchedFontNames);

      //fetch google font options
      if (googleOptions.length) {
        const googleFontRequests = googleOptions.map(option => fetchGoogleFont(option));
        Promise.all(googleFontRequests).then(() => {
          callback(null, {
            options: this.getLimitedOptions(limitedOptions)
          });
        }).catch(err => {
          throw err;
        });
      } else {
        callback(null, {
          options: this.getLimitedOptions(limitedOptions)
        });
      }
    }).catch(err => {
      throw err;
    });
  }
  loadOptions(input, callback, pagination) {
    const {
      assetPath,
      customFontsByAssetRequestStatus,
      customFontsRequestStatus,
      fontList,
      googleFontOptions,
      isEmailOrAnyType,
      limitedOptions
    } = this.props;
    if (this.fontListArray) {
      const fontOptions = Object.values(fontList).reduce((fontOptionsAcc, fontOption) => {
        const {
          options
        } = fontOption;

        // support 1-level grouped options
        if (Array.isArray(options)) {
          fontOptionsAcc.push({
            text: fontOption.text,
            options: options.map(this.getFontListOption)
          });
        } else {
          fontOptionsAcc.push(this.getFontListOption(fontOption));
        }
        return fontOptionsAcc;
      }, []);
      callback(null, {
        options: fontOptions
      });
      return;
    }
    if (isEmailOrAnyType) {
      callback(null, {
        options: this.systemFonts
      });
      return;
    }
    let offset = pagination.offset;

    // If you load a few pages of Google fonts and then close/reopen the select,
    // don't refetch any Google fonts. But if there's custom fonts to fetch,
    // we must do a refetch
    const inputHasNotChanged = input === this.state.input;
    const numGoogleFontsFetched = Object.keys(googleFontOptions).length;
    const hasAlreadyFetchedCustomFontsByAsset = !assetPath || customFontsByAssetRequestStatus === RequestStatus.SUCCEEDED;
    const hasAlreadyFetchedCustomFonts = customFontsRequestStatus === RequestStatus.SUCCEEDED;
    const shouldNotRefetchFontsGated = inputHasNotChanged && !offset && numGoogleFontsFetched && hasAlreadyFetchedCustomFontsByAsset;
    const shouldNotRefetchFonts = shouldNotRefetchFontsGated && hasAlreadyFetchedCustomFonts;
    if (shouldNotRefetchFonts) {
      offset = numGoogleFontsFetched;
      if (limitedOptions) {
        callback(null, {
          options: this.getLimitedOptions(limitedOptions)
        });
      } else {
        callback(null, {
          options: this.getGroupedOptions(input),
          pagination: {
            hasMore: this.hasMore,
            offset,
            length: this.total
          }
        });
      }
      return;
    }
    if (limitedOptions) {
      this.debouncedLoadLimitedOptionsWithFontFetching(limitedOptions, callback);
    } else {
      this.debouncedLoadOptionsWithFontFetching(input, callback, pagination);
    }
  }
  onFontSelect({
    target: {
      value: font
    }
  }) {
    const {
      customFontsByAsset,
      customFonts,
      fontList,
      onFontFamilyChange,
      selectCustomFont,
      selectGoogleFont
    } = this.props;
    let newFontObject = {
      font,
      fallback: null,
      variant: null,
      font_set: FONT_SETS.DEFAULT
    };
    if (!fontList && !SYSTEM_FONT_VALUES.includes(font)) {
      const customFontByAsset = customFontsByAsset[font];
      const customFont = customFonts[font];
      if (customFontByAsset) {
        selectCustomFont(customFontByAsset);
        const {
          variants,
          family,
          fallback,
          styles = {}
        } = customFontByAsset;
        this.trackCustomFontSelect(family, FONT_SETS.CUSTOM);
        const defaultVariant = variants[0];
        newFontObject = {
          styles: styles[defaultVariant],
          variant: defaultVariant,
          font: family,
          fallback,
          font_set: FONT_SETS.CUSTOM
        };
      } else if (customFont) {
        var _ref, _variants$;
        selectCustomFont(customFont);
        const {
          variants,
          name,
          fallback,
          id: fontId
        } = customFont;
        this.trackCustomFontSelect(name, FONT_SETS.BRANDING);
        const defaultVariant = (_ref = variants && ((_variants$ = variants[0]) === null || _variants$ === void 0 ? void 0 : _variants$.name)) !== null && _ref !== void 0 ? _ref : '';
        const styles = variants && variants.reduce((acc, variant) => {
          const {
            style,
            name: variantName
          } = variant;
          try {
            const styleObject = style && typeof style === 'string' ? JSON.parse(style) : {};
            acc[variantName] = styleObject;
          } catch (e) {
            console.error('Error parsing style object', e);
          }
          return acc;
        }, {});
        newFontObject = {
          styles: styles && styles[defaultVariant],
          variant: defaultVariant,
          font: name,
          fallback,
          font_set: FONT_SETS.BRANDING,
          fontId
        };
      } else {
        const googleFont = this.allGoogleFonts[font];
        selectGoogleFont(googleFont);
        const {
          variants,
          family,
          fallback
        } = googleFont;
        newFontObject = {
          styles: {},
          variant: getDefaultVariant(variants),
          font: family,
          fallback,
          font_set: FONT_SETS.GOOGLE
        };
      }
    }
    onFontFamilyChange(newFontObject);
  }

  //preview iframe needs to be disabled like this for file drag and drop to work overlaid on top of it as a modal

  handleCancelCustomUpload() {
    this.setState({
      customUploadModalOpen: false
    });
    this.togglePreviewPointerEvents('auto');
  }
  handleCustomUploadClick() {
    this.setState({
      customUploadModalOpen: true
    });
    this.togglePreviewPointerEvents('none');
  }
  renderDisplayFont(option) {
    const {
      commonGoogleFonts,
      customFontsByAsset,
      customFonts,
      fontList,
      googleFonts,
      selectedCustomFonts,
      selectedGoogleFonts
    } = this.props;
    const {
      buttonText,
      dropdownText,
      fontSet,
      text,
      value
    } = option;
    if (buttonText || dropdownText) return buttonText || dropdownText;
    const fontKey = fontList ? text : value;
    switch (fontSet) {
      case FONT_SETS.BRANDING:
        {
          const customFont = selectedCustomFonts[fontKey] || customFonts[fontKey];
          return /*#__PURE__*/_jsx(BrandingCustomFontOption, {
            font: customFont || {
              family: fontKey
            }
          });
        }
      case FONT_SETS.CUSTOM:
        {
          const customFont = selectedCustomFonts[fontKey] || customFontsByAsset[fontKey];
          return /*#__PURE__*/_jsx(CustomFontOption, {
            font: customFont || {
              family: fontKey
            }
          });
        }
      case FONT_SETS.GOOGLE:
        {
          const googleFont = selectedGoogleFonts[fontKey] || commonGoogleFonts[fontKey] || googleFonts[fontKey];
          return /*#__PURE__*/_jsx(GoogleFontOption, {
            font: googleFont || {
              family: fontKey
            }
          });
        }
      case FONT_SETS.DEFAULT:
        {
          const systemFont = SYSTEM_FONTS[text];
          return /*#__PURE__*/_jsx(DefaultFontOption, {
            fontFamily: systemFont,
            children: text
          });
        }
      default:
        return /*#__PURE__*/_jsx(DefaultFontOption, {
          fontFamily: value,
          children: text || value
        });
    }
  }
  renderFontPickerOption(option) {
    const {
      options,
      text,
      value,
      dropdownText,
      loading
    } = option;
    const isOptionGroupHeader = Array.isArray(options);
    const optionText = text || value;
    if (loading) {
      return /*#__PURE__*/_jsx(View, {
        paddingBlock: "200",
        children: dropdownText
      });
    }
    return /*#__PURE__*/_jsx("div", {
      "data-selenium-option": optionText,
      "data-option-value": value,
      role: "option",
      title: optionText,
      children: /*#__PURE__*/_jsx(Button, {
        marginInline: isOptionGroupHeader ? '500' : undefined,
        paddingBlock: isOptionGroupHeader ? '200' : undefined,
        tabIndex: -1,
        truncate: !isOptionGroupHeader,
        use: "unstyled",
        children: isOptionGroupHeader ? optionText : this.renderDisplayFont(option)
      })
    });
  }
  renderFontPickerValue(option) {
    return /*#__PURE__*/_jsx("span", {
      "data-option-value": option.value,
      role: "option",
      children: /*#__PURE__*/_jsx("span", {
        "data-option-text": true,
        children: this.renderDisplayFont(option)
      })
    });
  }
  renderFooterLinks(isEmailOrCustomFonts, customUploadPageToOpen) {
    const {
      customFontsRequestStatus,
      themePath,
      limitedOptions
    } = this.props;
    const googleFontsLink = /*#__PURE__*/_jsx(Text, {
      as: "small",
      children: /*#__PURE__*/_jsx(UILink, {
        external: true,
        href: "https://fonts.google.com/",
        "aria-label": unescapedText(i18nKey('googleFontsLink')),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('googleFontsLink')
        })
      })
    });
    return !limitedOptions && !isEmailOrCustomFonts && (customFontsRequestStatus === RequestStatus.SUCCEEDED && themePath ? /*#__PURE__*/_jsxs(List, {
      children: [/*#__PURE__*/_jsx(ListItem, {
        children: googleFontsLink
      }), /*#__PURE__*/_jsx(ListItem, {
        marginBlockStart: "200",
        children: /*#__PURE__*/_jsx(Text, {
          as: "small",
          children: /*#__PURE__*/_jsx(UILink, {
            onClick: this.handleCustomUploadClick,
            "data-selenium": `${this.seleniumKey}:customUploadLink`,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey(`uploadFont.${customUploadPageToOpen.toLowerCase()}`)
            })
          })
        })
      })]
    }) : googleFontsLink);
  }
  render() {
    const {
      error,
      value,
      readOnly,
      fontPickerProps,
      isEmailOrAnyType,
      fontList,
      useOnDark,
      customFonts,
      limitedOptions
    } = this.props;
    const {
      customUploadModalOpen
    } = this.state;
    const {
      font
    } = value || {};
    const isEmailOrCustomFonts = isEmailOrAnyType || !!fontList;
    const customUploadPageToOpen = customFonts && Object.keys(customFonts).length ? ModalPages.MANAGE : ModalPages.ADD;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(AsyncSelector, Object.assign({}, fontPickerProps, {
        "data-selenium": `${this.seleniumKey}:font`,
        defaultOptions: this.defaultOptions,
        dropdownClassName: "cmv2-font-picker-dropdown",
        dropdownFooter: this.renderFooterLinks(isEmailOrCustomFonts, customUploadPageToOpen),
        error: UNSUPPORTED_FONT_VALUES.includes(font) || error,
        loadOptions: this.loadOptions,
        menuStyle: {
          maxHeight: 325
        },
        onChange: this.onFontSelect,
        optionRenderer: this.renderFontPickerOption,
        placeholder: unescapedText(i18nKey('defaultButtonText')),
        readOnly: readOnly,
        searchable: !limitedOptions && !isEmailOrCustomFonts,
        type: FONT,
        useOnDark: useOnDark,
        value: this.font,
        valueRenderer: this.renderFontPickerValue
      })), customUploadModalOpen && /*#__PURE__*/_jsx(CustomFontsUploadModal, {
        onCancel: this.handleCancelCustomUpload,
        openingPage: customUploadPageToOpen
      })]
    });
  }
}
export default connector(FontFamilyPicker);